import React, { useEffect, useState } from "react";
import { Input, message, Table, TableProps } from "antd";
import { IInvoiceList } from "./types";
import { getColumns } from "./Columns";
import { mapInvoiceList } from "./Mapper";
import { InvoiceService } from "../../../services/Invoices/InvoiceService/InvoiceService";
import { CreatorProfileService } from "../../../services/Invoices/CreatorProfile/CreatorProfileService";
import PageWrapper from "../../../Layout/PageWrapper";
import useDebounce from "../../../hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import DiscardInvoiceModal from "./DisacrdInvoiceModal";

const InvoiceList: React.FC = () => {
	const navigate = useNavigate();
	
	const [loading, setLoading] = useState<boolean>(false);
	const [invoiceList, setInvoiceList] = useState<IInvoiceList[]>([]);
	const [loadingRows, setLoadingRows] = useState<Record<number, boolean>>({});
	const [loadingRowsDownload, setLoadingRowsDownload] = useState<Record<number, boolean>>({});
	const [loadingReminderRows, setLoadingReminderRows] = useState<Record<number, boolean>>({});
	const [totalRecords, setTotalRecords] = useState(0);
	const [searchValue, setSearchValue] = useState("");
	const [discardOpen, setDiscardOpen] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState<IInvoiceList | null>(null);
	const [params, setParams] = useState({
		pageNumber: 1,
		pageSize: 20,
		searchTerm: "",
		sortField: "",
		sortType: ""
	});

	const debouncedSearchTerm = useDebounce(searchValue, 500);

	useEffect(() => {
		setParams((prev) => ({
			...prev,
			searchTerm: debouncedSearchTerm,
			pageNumber: 1
		}));
	}, [debouncedSearchTerm]);

	useEffect(() => {
		getInvoiceList();
	}, [params]);

	const getInvoiceList = async () => {
		setLoading(true);
		try {
			const filteredParams = Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== "" && v !== undefined));
			const res = await InvoiceService.getInvoiceList(filteredParams);
			const filteredData = mapInvoiceList(res?.data?.data || []);
			setInvoiceList(filteredData);
			setTotalRecords(res.data?.totalRecords || 0);
		} catch (error: any) {
			if (error?.status === 429) message.error("Too many requests. Please try again later.");
			else message.error(error?.response?.data?.message || "Something went wrong");
		} finally {
			setLoading(false);
		}
	};

	const handleTableChange: TableProps<IInvoiceList>["onChange"] = (pagination, _, sorter) => {
		let sortField = "";
		let sortType = "";

		if (!Array.isArray(sorter)) {
			sortField = (sorter.field as string) || "";
			sortType = sorter.order === "ascend" ? "asc" : sorter.order === "descend" ? "desc" : "";
		}

		setParams({
			...params,
			pageNumber: pagination.current || 1,
			pageSize: pagination.pageSize || 20,
			sortField,
			sortType
		});
	};

	const handleView = async (record: IInvoiceList) => {
		setLoadingRows((prev) => ({ ...prev, [record.invoiceId]: true }));
		try {
			const { url } = await CreatorProfileService.getDocument({
				userId: record.userId,
				customerId: record.customerId,
				docType: "invoice",
				invoiceId: record.invoiceId
			});
			if (url) {
				window.open(url, "_blank", "noopener,noreferrer");
			} else {
				message.error("Document URL not available");
			}
		} catch (err) {
			message.error("Failed to load document preview");
		} finally {
			setLoadingRows((prev) => ({ ...prev, [record.invoiceId]: false }));
		}
	};

	const handleDownload = async (record: IInvoiceList) => {
		setLoadingRowsDownload((prev) => ({ ...prev, [record.invoiceId]: true }));

		try {
			const { url } = await CreatorProfileService.getDocument({
				userId: record.userId,
				customerId: record.customerId,
				docType: "invoice",
				invoiceId: record.invoiceId
			});

			if (!url) {
				message.error("Document URL not available");
				return;
			}

			const response = await fetch(url);
			if (!response.ok) {
				throw new Error("Failed to fetch file");
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = blobUrl;
			link.download = `invoice-${record.invoiceNumber}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			window.URL.revokeObjectURL(blobUrl);
		} catch (error) {
			message.error("Failed to download invoice");
		} finally {
			setLoadingRowsDownload((prev) => ({ ...prev, [record.invoiceId]: false }));
		}
	};

	const handleSendReminder = async (invoiceId: number) => {
		try {
			setLoadingReminderRows((prev) => ({
				...prev,
				[invoiceId]: true
			}));

			const response = await InvoiceService.sendInvoiceReminderEmail(invoiceId);

			if (response.success) {
				message.success(response.message);
			} else {
				message.error(response.message);
			}
		} catch (error: any) {
			message.error(error?.response?.data?.message || "Failed to send invoice reminder email.");
		} finally {
			setLoadingReminderRows((prev) => ({
				...prev,
				[invoiceId]: false
			}));
		}
	};
	
	const openDiscardModal = (record: IInvoiceList) => {
		setSelectedInvoice(record);
		setDiscardOpen(true);
	};
	
	const handleDiscardInvoice = async (comment: string) => {
		try {
			await InvoiceService.rejectInvoice(selectedInvoice!.invoiceId, "Discarded", comment);
			message.success("Invoice discarded");
			getInvoiceList();
		} catch (err) {
			message.error("Discard failed");
		} finally {
			setDiscardOpen(false);
		}
	};
	return (
		<PageWrapper title="Invoice List">
			<Input placeholder="Search invoices" onChange={(e) => setSearchValue(e.target.value)} style={{ width: 300, marginBottom: 16 }} />
			<Table<IInvoiceList>
				loading={loading}
				columns={getColumns({
					onView: handleView,
					onReject: openDiscardModal,
					loadingRows: loadingRows,
					loadingRowsDownload: loadingRowsDownload,
					loadingReminderRows: loadingReminderRows,
					onDownload: handleDownload,
					onSendReminder: handleSendReminder,
					navigate
				})}
				dataSource={invoiceList}
				pagination={{
					current: params.pageNumber,
					pageSize: params.pageSize,
					total: totalRecords,
					showSizeChanger: true,
					showQuickJumper: true
				}}
				onChange={handleTableChange}
				scroll={{ x: "max-content" }}
			/>
			<DiscardInvoiceModal
				open={discardOpen}
				invoiceNumber={selectedInvoice?.invoiceNumber}
				onCancel={() => setDiscardOpen(false)}
				onSubmit={handleDiscardInvoice}
			/>
		</PageWrapper>
	);
};

export default InvoiceList;
